import { assert } from '@sama/js-core';
import { Point2D } from '../../../../team-apis/otterdam/answers/shape-typings';
import { SingleFrameCalibrationData } from '../../../../team-apis/otterdam/calibration-data';
import { H3Material } from '../../../materials/basic/h3-basic-material';

export interface FisheyeProjectionUniforms {
  k: number[];
  imageSize: Point2D;
  focalLength: Point2D;
  principlePoint: Point2D;
}

export function setFisheyeUniforms(
  material: H3Material,
  config: {
    currentFrameCalibrationData: SingleFrameCalibrationData;
  },
) {
  const { distortion, focalLength, principlePoint } =
    config.currentFrameCalibrationData;
  assert(distortion.k, 'k coefficients are required for fisheye projection');

  material.setUniform('k', distortion.k.slice(0, 4));
  material.setUniform('focalLength', focalLength);
  material.setUniform('principlePoint', principlePoint);
}

export const fisheyeProjection = `
#if defined ( USE_FISHEYE_PROJECTION )

  vec4 getFisheyePosition(vec3 h3Position, mat4 h3ProjectionMatrix, mat4 h3ViewMatrix) {
    // [-1..1]
    vec4 pinhole = h3ProjectionMatrix * h3ViewMatrix * modelMatrix * vec4(h3Position, 1.0);
    vec2 undistorted = pinhole.xy / pinhole.w;

    // [0..imageSize]
    vec2 undistortedImage = (undistorted * imageSize + imageSize) / 2.0;

    vec2 xy = (undistortedImage - principlePoint) / focalLength;
    float r = length(xy);

    float theta = atan(r);
    float theta2 = theta * theta;
    float theta3 = theta2 * theta;
    float theta5 = theta3 * theta2;
    float theta7 = theta5 * theta2;
    float theta9 = theta7 * theta2;

    float theta_d = theta + k.x * theta3 + k.y * theta5 + k.z * theta7 + k.w * theta9;
    float scale = theta_d / r;

    // [0..imageSize]
    vec2 distortedImage = principlePoint + xy * scale * focalLength;

    // [-1..1]
    vec2 finalPoint = (distortedImage / imageSize) * 2.0 - 1.0;
    return vec4(finalPoint * pinhole.w, pinhole.z, pinhole.w);
  }

#endif
`;
